/**
 * Corroboration + confidence visuals for FIM subjects — the shared, canonical
 * copy used by the Dossiers subject lenses.
 *
 * Corroboration is shown by SOURCE TYPE (breadth) — a fixed slot per known
 * source type, filled when that type asserts the subject. Breadth (how many
 * *independent kinds* of evidence agree) is the trust signal; it stays bounded
 * as the number of source *lists* grows because the glyph is keyed on type.
 *
 * `pages/fim/Corroboration.tsx` mirrors these visuals for the legacy internal
 * surface (it cannot import from here without dragging its page-local tone
 * helpers along); keep the two in sync until `/fim/review` retires.
 */
import type { SourceName } from 'features/fim'
import styled from 'styled-components'

// Tone colors matching `pages/fim/FimUi` (TONE_COLOR) — the subset the two
// glyphs below need.
const GREEN = '#34d399'
const AMBER = '#fbbf24'
const ROSE = '#fb7185'
const INDIGO = '#818cf8'
const SKY = '#38bdf8'
const MUTED = '#a1a1aa'

/** Canonical source types in a fixed slot order (keyed on type, not list). */
const SOURCE_TYPES: SourceName[] = [
  'cmms',
  'doc_nav',
  'agentic',
  'system_documents',
]

const SOURCE_COLOR: Record<SourceName, string> = {
  cmms: INDIGO,
  doc_nav: GREEN,
  agentic: AMBER,
  system_documents: SKY,
}

// The `agentic` source type is Mohak's process-data pipeline (per-PI-tag
// dossiers fused in as Source D) — label it by what it is, not how it's made.
const SOURCE_LABEL: Record<SourceName, string> = {
  cmms: 'CMMS',
  doc_nav: 'Doc Nav',
  agentic: 'Process data',
  system_documents: 'System documents',
}

/** Existence-confidence band → dot/meter color (green ≥85%, amber ≥60%, else rose). */
export function confidenceBandColor(n: number | null | undefined): string {
  if (n === null || n === undefined) return MUTED
  if (n >= 0.85) return GREEN
  if (n >= 0.6) return AMBER
  return ROSE
}

export function fmtConfidence(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—'
  return `${Math.round(n * 100)}%`
}

/** Title-case a snake/lower value ("bfw_pump" → "Bfw pump"). */
export function prettify(value: string | null | undefined): string {
  if (!value) return ''
  return value.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase())
}

const GlyphRow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
`

const Slots = styled.span`
  display: inline-flex;
  gap: 0.18rem;
`

const Slot = styled.span<{ $on: boolean; $color: string }>`
  width: 0.7rem;
  height: 0.7rem;
  border-radius: 0.2rem;
  background: ${({ $on, $color }) => ($on ? $color : 'transparent')};
  border: 1px solid
    ${({ $on, $color, theme }) => ($on ? $color : theme.forecast.border)};
`

const Caption = styled.span`
  font-size: 0.68rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
  white-space: nowrap;
`

interface CorroborationGlyphProps {
  /** The subject's corroborating source types (`fim-subject.source_set`). */
  sources: SourceName[]
  /** Show the "N-source" caption next to the slots. */
  showCaption?: boolean
}

/** Breadth-of-corroboration glyph: one slot per source type, filled if present. */
export function CorroborationGlyph({
  sources,
  showCaption = true,
}: CorroborationGlyphProps) {
  const present = new Set(sources)
  const hit = SOURCE_TYPES.filter((t) => present.has(t))
  const title =
    hit.length > 0
      ? `Corroborated by ${hit.length} of ${SOURCE_TYPES.length} source types: ${hit
          .map((t) => SOURCE_LABEL[t])
          .join(', ')}`
      : 'No source corroboration'
  return (
    <GlyphRow title={title}>
      <Slots>
        {SOURCE_TYPES.map((t) => (
          <Slot key={t} $on={present.has(t)} $color={SOURCE_COLOR[t]} />
        ))}
      </Slots>
      {showCaption && <Caption>{hit.length}-source</Caption>}
    </GlyphRow>
  )
}

const MeterWrap = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
`

const Track = styled.span`
  position: relative;
  display: inline-block;
  width: 3rem;
  height: 0.35rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  overflow: hidden;
`

const Fill = styled.span<{ $pct: number; $color: string }>`
  position: absolute;
  inset: 0 auto 0 0;
  width: ${({ $pct }) => $pct}%;
  background: ${({ $color }) => $color};
`

const Pct = styled.span<{ $color: string }>`
  font-size: 0.68rem;
  font-weight: 600;
  color: ${({ $color }) => $color};
  font-variant-numeric: tabular-nums;
`

interface ConfidenceMeterProps {
  value: number | null | undefined
}

/** Existence confidence as a band-colored meter + exact %. */
export function ConfidenceMeter({ value }: ConfidenceMeterProps) {
  const color = confidenceBandColor(value)
  const pct = Math.round((value ?? 0) * 100)
  return (
    <MeterWrap title={`existence confidence ${fmtConfidence(value)}`}>
      <Track>
        <Fill $pct={pct} $color={color} />
      </Track>
      <Pct $color={color}>{fmtConfidence(value)}</Pct>
    </MeterWrap>
  )
}
